import { useCallback } from 'react';
import { reportAppointmentsChanged } from '../lib/appointmentsSignal';
import { isOnlineSource } from '../constants/appointmentSource';
import { useAccess } from './useAccess.js';
import { useClinicEvents } from './useClinicEvents.js';

/**
 * «نوبت آنلاین جدید» — hearing that a patient booked themselves (PM-0382,
 * docs/DOCTOR_PAGE_INSTANT_BOOKING_PLAN.md §4.8).
 *
 * Since 2026-10-19 a booking made in the patient app or on the doctor's public page
 * is confirmed on the spot: no request reaches «درخواست‌های نوبت», nobody at the
 * desk presses anything, and the row simply appears. This hook is how the desk finds
 * out. It listens for `appointment.created` on the clinic's realtime channel and,
 * for an ONLINE source only:
 *
 *   1. claims the appointment id — once per id, however many frames carry it
 *      (two desks, a reconnect, the same event re-published) — so one booking is
 *      announced once;
 *   2. tells the day list to re-read (`reportAppointmentsChanged`), so the row —
 *      the patient's name included, behind appointments.view — is there when the
 *      operator looks;
 *   3. hands `{ id, dateTime, source }` to `onAnnounce`, which words it.
 *
 * NO PATIENT DATA reaches the announcement (DESIGN_CONCEPT §7, §13.2: a toast
 * carries no PHI). The day and the hour come from the hub's own nudge
 * (`date_time`), so there is nothing to read and nothing to leak; whatever else a
 * frame might carry is never passed on.
 *
 * Only for someone who may see appointments: the gate is the same key as the day
 * list. A booking the DESK confirmed (a request answered with «تأیید» — its
 * `confirmed_by` is a person, not `auto`) is not announced: the person who pressed
 * the button knows.
 *
 * Logic only, no DOM: the toast is OnlineBookingToaster's.
 */

export const ONLINE_BOOKING_EVENTS = Object.freeze(['appointment.created']);

// Module-level, so a remount of the shell or a second listener does not announce
// the same booking twice. Bounded: an open panel runs for a whole shift.
const MAX_REMEMBERED = 200;
const announced = new Set();

/** True the first time an id is seen; false for every repeat. */
export function claimAnnouncement(id) {
  const key = String(id ?? '').trim();
  if (!key || announced.has(key)) return false;
  announced.add(key);
  if (announced.size > MAX_REMEMBERED) announced.delete(announced.values().next().value);
  return true;
}

/** Test seam — forgets every id. */
export function resetOnlineBookingAnnouncements() {
  announced.clear();
}

/**
 * The appointment a frame is about. The hub's nudge names it `appointment_id`; a
 * BFF one release behind still forwarded the whole body, where it was `id`.
 */
export const appointmentIdOf = (evt) => String(evt?.appointment_id ?? evt?.id ?? '').trim();

/** Is this frame a patient's own new booking that nobody at the desk made? */
export function isOnlineBookingEvent(evt) {
  if (!evt || evt.type !== 'appointment.created') return false;
  if (!isOnlineSource(evt.source)) return false;
  const by = String(evt.confirmed_by ?? evt.confirmedBy ?? '').trim();
  return by === '' || by === 'auto';
}

export function useOnlineBookingAnnouncements(onAnnounce) {
  const access = useAccess() || {};
  const enabled = Boolean(access.isAdmin || access.can?.('appointments.view'));

  const handle = useCallback((evt) => {
    if (!isOnlineBookingEvent(evt)) return;
    const id = appointmentIdOf(evt);
    if (!claimAnnouncement(id)) return;
    reportAppointmentsChanged();
    const when = Number(evt.date_time);
    // Exactly these three — never a field the frame happens to carry besides.
    onAnnounce?.({
      id,
      dateTime: Number.isFinite(when) && when > 0 ? when : null,
      source: String(evt.source),
    });
  }, [onAnnounce]);

  useClinicEvents(ONLINE_BOOKING_EVENTS, handle, { enabled });
  return { enabled };
}

export default useOnlineBookingAnnouncements;
